import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@components/atoms/button';
import { CheckMarkIcon } from '@components/icons/check-mark-icon';
import { ErrorMarkIcon } from '@components/icons/error-mark-icon';
import { useQueryParam } from '@hooks/use-query';
import { DocsVerificationStatusEnum } from '@models/enums';
import { useLazyGetVerificationByIdQuery } from '@modules/dashboard/api/verifications-api';
import type { FinishStepProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/docs-verification-steps/finish/types';
import type { RootState } from '@store/root-reducer';

import { PreviewButton } from './preview-button';
import { FinishStepContainer } from './styles';

const fetchDelay = 10000;

export const FinishStep: React.FC<FinishStepProps> = ({ onFinish }) => {
    const { t } = useTranslation();
    const organizationId = useSelector(
        (state: RootState) => state.dashboard?.session?.organizationId,
    );
    const { sdkOptions, verificationId } = useSelector(
        (state: RootState) => state.docsVerification,
    );
    const [getVerificationById, { data }] = useLazyGetVerificationByIdQuery();
    const isFinished = useRef(false);
    const [timeToRedirect, setTimeToRedirect] = useState(5000);
    const queryParam = useQueryParam();
    const botName = queryParam.search.get('botName');

    const isUseSDK = !organizationId;

    const redirectToLink = useCallback(() => {
        if (
            sdkOptions?.redirectUrl ||
            sdkOptions?.settings?.finish_screen_settings?.redirect_link
        ) {
            window.location.href =
                (sdkOptions.redirectUrl as string) ??
                sdkOptions?.settings?.finish_screen_settings?.redirect_link;
        }
    }, [sdkOptions?.settings?.finish_screen_settings?.redirect_link, sdkOptions?.redirectUrl]);

    const startRedirectTimer = useCallback(() => {
        let time = timeToRedirect;
        const checkTime = () => {
            if (time === 0) {
                redirectToLink();
            } else {
                time -= 1000;
                setTimeToRedirect(time);
            }
            setTimeout(() => {
                checkTime();
            }, 1000);
        };

        setTimeout(() => {
            checkTime();
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdkOptions]);

    useEffect(() => {
        const handleFinish = async () => {
            if (onFinish) {
                await onFinish();
            }
            if (
                isUseSDK &&
                (sdkOptions?.redirectUrl ||
                    sdkOptions?.settings?.finish_screen_settings?.redirect_link)
            ) {
                startRedirectTimer();
            }
        };

        if (!isFinished.current) {
            isFinished.current = true;
            handleFinish();
        }
    }, [sdkOptions, onFinish, startRedirectTimer, isUseSDK]);

    useEffect(() => {
        if (verificationId && !isUseSDK) {
            getVerificationById({
                organizationId,
                id: verificationId,
            });
        }
    }, [getVerificationById, organizationId, verificationId, isUseSDK]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (
                data?.status === DocsVerificationStatusEnum.PENDING ||
                data?.status === DocsVerificationStatusEnum.IN_PROGRESS
            ) {
                if (!isUseSDK) {
                    getVerificationById({
                        organizationId,
                        id: verificationId,
                    });
                }
            } else {
                clearInterval(interval);
            }
        }, fetchDelay);

        return () => {
            clearInterval(interval);
        };
    }, [data?.status, getVerificationById, organizationId, verificationId, isUseSDK]);

    const actualIcon = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return <CheckMarkIcon />;
            case DocsVerificationStatusEnum.FAILED:
                return <ErrorMarkIcon />;
            default:
                return <CheckMarkIcon />;
        }
    }, [data]);

    const actualTitle = useMemo(() => {
        switch (data?.status) {
            case DocsVerificationStatusEnum.VERIFIED:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? sdkOptions?.settings?.finish_screen_settings?.title
                    : t('common.thankYou');
            case DocsVerificationStatusEnum.FAILED:
                return t('docVerificationWizard.finish.title.failed');
            default:
                return sdkOptions?.settings?.finish_screen_settings?.enabled
                    ? sdkOptions?.settings?.finish_screen_settings?.title
                    : t('common.thankYou');
        }
    }, [
        data?.status,
        sdkOptions?.settings?.finish_screen_settings?.title,
        sdkOptions?.settings?.finish_screen_settings?.enabled,
        t,
    ]);

    const isTelegram = sdkOptions?.source === 'telegram';

    return (
        <FinishStepContainer>
            {isUseSDK && (
                <React.Fragment>
                    {sdkOptions?.settings?.finish_screen_settings?.enabled ? (
                        <React.Fragment>
                            <div className='icon icon-success' style={{ textAlign: 'center' }}>
                                <img
                                    src='https://static.dataspike.io/images/sdk/verification/success-icon.png'
                                    alt='Success icon'
                                />
                            </div>
                            <div className='header'>
                                <h3 className='finish-title'>
                                    {sdkOptions?.settings?.finish_screen_settings.title}
                                </h3>
                                <p className='finish-description'>
                                    {sdkOptions?.settings?.finish_screen_settings.main_text}
                                </p>
                            </div>
                            {sdkOptions?.settings?.finish_screen_settings.redirect_link && (
                                <React.Fragment>
                                    {sdkOptions?.settings?.finish_screen_settings
                                        ?.redirect_warning ? (
                                        <div className='finish-description'>
                                            {
                                                sdkOptions?.settings?.finish_screen_settings
                                                    ?.redirect_warning
                                            }
                                        </div>
                                    ) : (
                                        <div
                                            className='finish-description'
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'docVerificationWizard.finish.description.youWillBeRedirected',
                                                    {
                                                        count: timeToRedirect / 1000,
                                                    },
                                                ),
                                            }}
                                        />
                                    )}

                                    <Button
                                        variant='primary'
                                        className='button-redirect'
                                        onClick={() => redirectToLink()}
                                        size='xl'
                                    >
                                        {sdkOptions?.settings?.finish_screen_settings.cta ||
                                            t('common.goBack')}
                                    </Button>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div className='icon icon-success' style={{ textAlign: 'center' }}>
                                <img
                                    src='https://static.dataspike.io/images/sdk/verification/success-icon.png'
                                    alt='Success icon'
                                />
                            </div>
                            <div className='header'>
                                <h3 className='finish-title'>
                                    <span>{t('common.thankYou')}</span>
                                    {'\n'}
                                    {!isTelegram && (
                                        <span className='alt-title'>
                                            {t(
                                                'docVerificationWizard.finish.title.youUploadedAllDocuments',
                                            )}
                                        </span>
                                    )}
                                </h3>
                                {sdkOptions?.redirectUrl && (
                                    <React.Fragment>
                                        <div
                                            className='finish-description'
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'docVerificationWizard.finish.description.youWillBeRedirected',
                                                    {
                                                        count: timeToRedirect / 1000,
                                                    },
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant='primary'
                                            className='button-redirect'
                                            onClick={() => redirectToLink()}
                                            size='xl'
                                        >
                                            {t('common.goBack')}
                                        </Button>
                                    </React.Fragment>
                                )}
                                {!sdkOptions?.redirectUrl && isTelegram && (
                                    <React.Fragment>
                                        <p className='finish-description'>
                                            {t('docVerificationWizard.finish.description.telegram')}
                                        </p>
                                        {!!botName && (
                                            <Button
                                                variant='primary'
                                                className='button-redirect'
                                                size='xl'
                                                to={`tg://resolve?domain=${botName}`}
                                            >
                                                {t('docVerificationWizard.finish.goToTelegram')}
                                            </Button>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {!isUseSDK && (
                <React.Fragment>
                    <div className='icon'>{actualIcon}</div>
                    <div className='header'>
                        <h3 className='finish-title'>{actualTitle}</h3>
                        <p className='finish-description'>
                            {sdkOptions?.settings?.finish_screen_settings?.enabled
                                ? sdkOptions?.settings?.finish_screen_settings?.main_text
                                : t('docVerificationWizard.finish.title.youUploadedAllDocuments')}
                        </p>
                        <p className='finish-description'>
                            {!sdkOptions?.settings?.finish_screen_settings?.enabled &&
                                t('docVerificationWizard.finish.subtitle.youUploadedAllDocuments')}
                        </p>
                    </div>
                    <PreviewButton />
                </React.Fragment>
            )}
        </FinishStepContainer>
    );
};
